import React from "react"
import { RequestError } from "../../shared/RequestError"
import { FeedSpinner } from "../../shared/Spinner"
import { ErrorBoundary } from "../../shared/ErrorBoundary"
import { ConcertListItemShort } from "../../posts/ConcertListItemShort"
import { mutationRequest } from "../../../util/request"
import { useInfiniteBandPosts } from "../../../hooks/useBandPosts"
import type { ConcertDisplayConfig } from "../../../types/concertDisplay"

interface ConcertSectionProps {
  bandId: number
  bandName: string
  contentType: "future_concerts" | "past_concerts"
  title?: string
  noResultsMessage?: string
  pageSize?: number
  adminMode?: boolean
  config: ConcertDisplayConfig
}

const ConcertSection = ({
  bandId,
  bandName,
  contentType,
  title,
  noResultsMessage,
  pageSize = 20,
  adminMode = false,
  config,
}: ConcertSectionProps) => {
  const effectivePageSize = adminMode ? 12 : pageSize
  const { data, error, fetchNextPage, isFetching, status } = useInfiniteBandPosts({
    bandId,
    contentType,
    adminMode,
    pageSize: effectivePageSize,
    ownerOnly: undefined,
  })
  const lastPage = status === "success" ? data.pages.slice(-1)[0] : null

  return (
    <ErrorBoundary>
      <div id={contentType}>
        {status === "loading" ? (
          <FeedSpinner />
        ) : status === "error" ? (
          <RequestError error={error} />
        ) : (
          <>
            {data.pages[0].total_count === 0 && noResultsMessage && (
              <p className="text-gray-500 text-sm py-4">{noResultsMessage}</p>
            )}
            {data.pages[0].total_count > 0 && (
              <>
                {title && (
                  <h2 className="border-b-4 border-black font-bold my-4 text-2xl uppercase">
                    {title}
                  </h2>
                )}
                <ul className="grid grid-cols-1 gap-8">
                  {data.pages.map((page) => (
                    <React.Fragment key={page.page}>
                      {page.posts.map((concert: any) => (
                        <ConcertSectionItem
                          key={`${concert.id}:${adminMode}`}
                          concert={concert}
                          adminMode={adminMode}
                          config={config}
                        />
                      ))}
                    </React.Fragment>
                  ))}
                </ul>
                {lastPage?.has_next_page && (
                  <button
                    className="text-blue-600 focus:outline-none mt-4"
                    onClick={() => fetchNextPage()}
                    disabled={isFetching}
                  >
                    {isFetching
                      ? "loading..."
                      : `See more ${(title ?? "shows").toLowerCase()} from ${bandName}`}
                  </button>
                )}
              </>
            )}
          </>
        )}
      </div>
    </ErrorBoundary>
  )
}

// Isolated so each item manages its own optimistic status update
function ConcertSectionItem({
  concert: initialConcert,
  adminMode,
  config,
}: {
  concert: any
  adminMode: boolean
  config: ConcertDisplayConfig
}) {
  const [concert, setConcert] = React.useState(initialConcert)

  const updateStatus = async () => {
    try {
      if (concert.status === "published") {
        const response = await mutationRequest({
          path: `/api/v1/bands/${concert.band_id}/posts/${concert.id}`,
          options: { method: "DELETE" },
        })
        if (response.success) setConcert({ ...concert })
      } else {
        const response = await mutationRequest({
          path: `/api/v1/bands/${concert.band_id}/posts/${concert.id}`,
          options: { method: "PUT" },
          data: { post: { status: "published" } },
        })
        if (response.success) setConcert({ ...concert })
      }
    } catch (error) {
      console.error("ERROR: ", error)
    }
  }

  return (
    <li id={concert.id} data-post-type={concert.post_type} className="mb-8 sm:mb-12">
      <ConcertListItemShort
        concert={concert}
        adminMode={adminMode}
        updateStatus={updateStatus}
        config={config}
      />
    </li>
  )
}

export default ConcertSection
